/**
 * PrivacyControls — RGPD self-service widget for the user dashboard.
 *
 * Two actions:
 *   - Export my data (Art. 20) → downloads a JSON dump from /api/me/export
 *   - Delete my account (Art. 17) → requires typing the confirmation phrase
 *
 * Admins see a notice that their account cannot be self-deleted (must go
 * through the DPO) — kept consistent with the backend guard.
 */
import { useState } from "react";
import { Download, Trash2, AlertTriangle, Loader2, X, ShieldCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CONFIRMATION_PHRASE = "SUPPRIMER MON COMPTE";

function DeleteAccountModal({ open, onClose }) {
  const [phrase, setPhrase] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const valid = phrase === CONFIRMATION_PHRASE;

  const submit = async () => {
    setLoading(true);
    setError(null);
    try {
      await apiClient.delete("/me", {
        data: { confirmation: phrase, reason: reason || null },
      });
      // Force a full reload so AuthContext picks up the deleted session
      window.location.href = "/?goodbye=1";
    } catch (e) {
      setError(e?.response?.data?.detail || "Suppression impossible.");
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0B3A2A]/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="delete-account-modal"
    >
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-red-600">Action irréversible</p>
            <h3 className="font-serif italic text-lg text-[#0B3A2A] mt-1">
              Supprimer votre compte
            </h3>
          </div>
          <button onClick={onClose} aria-label="Fermer" className="text-slate-400 hover:text-slate-700 p-1">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 text-sm text-slate-700">
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
            <p className="text-[12px] text-red-900 leading-relaxed">
              Vos données d'identification (nom, prénom, email, téléphone) seront
              <strong> immédiatement pseudonymisées</strong>. Vos pièces comptables
              (factures) sont conservées 10 ans conformément à la loi française
              (Art. L123-22 Code de commerce) mais ne permettent plus de vous
              identifier directement.
            </p>
          </div>

          <div>
            <label htmlFor="delete-reason" className="block text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-1">
              Motif (optionnel)
            </label>
            <textarea
              id="delete-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Aide-nous à nous améliorer…"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-red-500"
              data-testid="delete-reason-input"
            />
          </div>

          <div>
            <label htmlFor="delete-phrase" className="block text-[12px] text-slate-700 mb-1">
              Pour confirmer, tapez exactement <strong>« {CONFIRMATION_PHRASE} »</strong> :
            </label>
            <input
              id="delete-phrase"
              type="text"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              autoComplete="off"
              spellCheck="false"
              className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none ${
                valid ? "border-red-500" : "border-slate-200 focus:border-red-500"
              }`}
              data-testid="delete-phrase-input"
            />
          </div>

          {error && (
            <p className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            data-testid="delete-cancel-btn"
          >
            Annuler
          </Button>
          <Button
            type="button"
            disabled={!valid || loading}
            onClick={submit}
            data-testid="delete-confirm-btn"
            className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Suppression…</> :
                       <><Trash2 className="w-4 h-4 mr-2" /> Supprimer définitivement</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function PrivacyControls() {
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const r = await apiClient.get("/me/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `confilex-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(e?.response?.data?.detail || "Export impossible.");
    } finally {
      setExporting(false);
    }
  };

  const isAdmin = user?.role === "admin";

  return (
    <Card className="p-5 rounded-2xl border-slate-100" data-testid="privacy-controls-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#C9A961]/15 flex items-center justify-center shrink-0">
          <ShieldCheck className="w-5 h-5 text-[#8A6E3A]" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961]">RGPD · Vos droits</p>
          <h3 className="font-serif italic text-lg text-[#0B3A2A] mt-1">
            Confidentialité de votre compte
          </h3>
          <p className="text-[12px] text-slate-500 mt-1 leading-relaxed">
            Exercez vos droits d'accès (Art. 15), de portabilité (Art. 20) et
            d'effacement (Art. 17) en toute autonomie.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-xl border border-slate-100 bg-slate-50/50">
          <div className="flex-1">
            <p className="text-sm font-medium text-[#0B3A2A]">Exporter mes données</p>
            <p className="text-[12px] text-slate-500">
              Téléchargez l'intégralité de vos informations au format JSON.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exporting}
            data-testid="privacy-export-btn"
            className="rounded-full"
          >
            {exporting ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> Génération…</> :
                         <><Download className="w-3.5 h-3.5 mr-2" /> Exporter</>}
          </Button>
        </div>
        {exportError && (
          <p className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {exportError}
          </p>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-xl border border-red-100 bg-red-50/40">
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">Supprimer mon compte</p>
            <p className="text-[12px] text-red-700/80">
              {isAdmin
                ? "Les comptes administrateurs ne peuvent pas être supprimés via l'auto-service. Contactez le DPO."
                : "Pseudonymisation immédiate. Action irréversible."}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setDeleteOpen(true)}
            disabled={isAdmin}
            data-testid="privacy-delete-btn"
            className="rounded-full border-red-300 text-red-700 hover:bg-red-100 disabled:opacity-40"
          >
            <Trash2 className="w-3.5 h-3.5 mr-2" />
            Supprimer
          </Button>
        </div>
      </div>

      <DeleteAccountModal open={deleteOpen} onClose={() => setDeleteOpen(false)} />
    </Card>
  );
}
